import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Database,
  Filter,
  Loader2,
  CheckSquare,
  Square,
  Search,
  X,
  ChevronDown,
  Copy,
  Check,
  Lock,
  Activity,
  Clock,
  ShieldAlert,
  BarChart2,
  Info,
  Flame,
  HardDrive,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import { DBExportModal, type ColumnItem } from './DBExportModal';

const slowQueriesExportColumns: ColumnItem[] = [
  { key: 'database', label: 'Database' },
  { key: 'username', label: 'User' },
  { key: 'query', label: 'Query Text' },
  { key: 'calls', label: 'Calls' },
  { key: 'avg_time_ms', label: 'Avg Time (ms)' },
  { key: 'total_time_ms', label: 'Total Time (ms)' },
];

interface QueriesViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const PALETTE = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#14b8a6', // teal
  '#f97316', // orange
  '#84cc16', // lime
  '#6366f1', // indigo
];

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full inline-block shrink-0 shadow-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span
              className="text-gray-700 dark:text-gray-200 text-xs truncate max-w-[160px] font-mono font-medium"
              title={entry.value}
            >
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const QueriesView: React.FC<QueriesViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
}) => {
  const effectiveDbType = (dbType || '').toLowerCase();
  const isClickHouse =
    ['clickhouse', 'click_house', 'click house'].includes(effectiveDbType) ||
    effectiveDbType.includes('clickhouse') ||
    (activeCluster || '').toLowerCase().includes('clickhouse');

  const isMariaDB =
    ['mariadb', 'maria_db', 'maria db'].includes(effectiveDbType) ||
    effectiveDbType.includes('mariadb') ||
    (activeCluster || '').toLowerCase().includes('mariadb');

  const isOracle =
    ['oracle'].includes(effectiveDbType) ||
    effectiveDbType.includes('oracle') ||
    (activeCluster || '').toLowerCase().includes('oracle');

  const [loading, setLoading] = useState(true);
  const [topQueries, setTopQueries] = useState<any[]>([]);
  const [topQueriesReason, setTopQueriesReason] = useState<string | null>(null);
  const [activeQueries, setActiveQueries] = useState<any[]>([]);
  const [queriesGraphData, setQueriesGraphData] = useState<any>(null);
  const [slowQueriesInfo, setSlowQueriesInfo] = useState<any>(null);
  const [slowQueries, setSlowQueries] = useState<any[]>([]);
  const [blocksData, setBlocksData] = useState<any>(null);

  // Search & Filter States
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeQuerySearch, setActiveQuerySearch] = useState<string>('');
  const [slowQuerySearch, setSlowQuerySearch] = useState<string>('');
  const [blockSearchQuery, setBlockSearchQuery] = useState<string>('');
  const [graphFilter, setGraphFilter] = useState<'all' | 'slow' | 'deadlocks' | 'locks'>('all');
  const [blockMetricTab, setBlockMetricTab] = useState<'aggregate' | 'shared_blks_hit' | 'shared_blks_read' | 'shared_blks_dirtied'>('aggregate');
  const [selectedBlockDb, setSelectedBlockDb] = useState<string>('all');
  const [hoveredGraphSeries, setHoveredGraphSeries] = useState<string | null>(null);
  const [hoveredBlockSeries, setHoveredBlockSeries] = useState<string | null>(null);

  const [isDbDropdownOpen, setIsDbDropdownOpen] = useState<boolean>(false);
  const [isBlockDbDropdownOpen, setIsBlockDbDropdownOpen] = useState<boolean>(false);
  const [blockDbFilterQuery, setBlockDbFilterQuery] = useState<string>('');
  const [selectedQueryForModal, setSelectedQueryForModal] = useState<any | null>(null);
  const [copiedQuery, setCopiedQuery] = useState(false);

  const handleCopyQuery = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedQuery(true);
    setTimeout(() => setCopiedQuery(false), 2000);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 192 });

  const blockDbDropdownRef = useRef<HTMLDivElement>(null);
  const blockDbButtonRef = useRef<HTMLButtonElement>(null);
  const [blockDbCoords, setBlockDbCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 220 });

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 192),
      });
    }
    if (blockDbButtonRef.current) {
      const rect = blockDbButtonRef.current.getBoundingClientRect();
      setBlockDbCoords({
        top: rect.bottom + window.scrollY + 4,
        left: Math.min(rect.left + window.scrollX, window.innerWidth - 240),
        width: Math.max(rect.width, 220),
      });
    }
  }, []);

  const toggleDbDropdown = () => {
    if (!isDbDropdownOpen) {
      updateCoords();
    }
    setIsDbDropdownOpen(!isDbDropdownOpen);
  };

  const toggleBlockDbDropdown = () => {
    if (!isBlockDbDropdownOpen) {
      updateCoords();
    }
    setIsBlockDbDropdownOpen(!isBlockDbDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDbDropdownOpen(false);
      }
      if (
        blockDbDropdownRef.current &&
        !blockDbDropdownRef.current.contains(event.target as Node) &&
        blockDbButtonRef.current &&
        !blockDbButtonRef.current.contains(event.target as Node)
      ) {
        setIsBlockDbDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [updateCoords]);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const activeParams: any = {
      cluster_id: activeCluster,
      
      window_minutes: minutes,
    };
    const topParams: any = {
      cluster_id: activeCluster,
      
    };
    const graphParams: any = {
      cluster_id: activeCluster,
      
      minutes,
    };

    if (clientId) {
      activeParams.client_id = clientId;
      topParams.client_id = clientId;
      graphParams.client_id = clientId;
    }

    try {
      const [topRes, activeRes, graphQueriesRes, perDbBlocksRes] = await Promise.allSettled([
        baseapi.get('/db_monitor_dashboard/top_queries', { params: topParams }),
        baseapi.get('/db_monitor_dashboard/active_queries', { params: activeParams }),
        baseapi.get('/db_monitor_dashboard/graph/queries', { params: graphParams }),
        !isClickHouse && !isOracle
          ? baseapi.get('/db_monitor_dashboard/graph/per_db/blocks', { params: graphParams })
          : Promise.resolve({ data: null }),
      ]);

      if (topRes.status === 'fulfilled') {
        const data = topRes.value.data?.data || (Array.isArray(topRes.value.data) ? topRes.value.data : []);
        setTopQueries(Array.isArray(data) ? data : []);
        const reason = typeof topRes.value.data?.reason === 'string' ? topRes.value.data.reason : (topRes.value.data?.reason ? String(topRes.value.data.reason) : null);
        setTopQueriesReason(reason && reason.trim() ? reason : null);
      } else {
        setTopQueries([]);
        setTopQueriesReason(null);
      }

      if (activeRes.status === 'fulfilled') {
        const aData = activeRes.value.data?.data || (Array.isArray(activeRes.value.data) ? activeRes.value.data : []);
        setActiveQueries(Array.isArray(aData) ? aData : []);
      } else {
        setActiveQueries([]);
      }

      if (graphQueriesRes.status === 'fulfilled') {
        const raw = graphQueriesRes.value.data;
        const gData = raw?.data || null;
        const sData = raw?.slow_queries || null;
        
        const mergedSlowQueriesInfo = sData
          ? {
              ...sData,
              available: sData.available !== undefined ? sData.available : (raw?.available !== undefined ? raw.available : true),
              reason: sData.reason || raw?.reason || '',
            }
          : raw?.available === false
            ? { available: false, reason: raw.reason || '' }
            : null;

        setQueriesGraphData(gData);
        setSlowQueriesInfo(mergedSlowQueriesInfo);
        const sqList = Array.isArray(sData?.data) ? sData.data : (Array.isArray(sData) ? sData : []);
        setSlowQueries(sqList);
      } else {
        setQueriesGraphData(null);
        setSlowQueriesInfo(null);
        setSlowQueries([]);
      }

      if (perDbBlocksRes.status === 'fulfilled') {
        const bData = perDbBlocksRes.value.data?.data || perDbBlocksRes.value.data || null;
        setBlocksData(bData);
      } else {
        setBlocksData(null);
      }
    } catch (err) {
      console.warn('QueriesView fetch error:', err);
      setTopQueries([]);
      setTopQueriesReason(null);
      setActiveQueries([]);
      setQueriesGraphData(null);
      setSlowQueriesInfo(null);
      setSlowQueries([]);
      setBlocksData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, isClickHouse, isOracle]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Graph Telemetry Formatting
  const graphLabels: string[] = queriesGraphData?.labels || [];
  const graphSeries: Record<string, (number | null)[]> = queriesGraphData?.series || {};
  const graphSummary = queriesGraphData?.summary;
  const isSlowQueriesAvailable = slowQueriesInfo?.available !== false && queriesGraphData?.available !== false;
  const slowQueriesReason = slowQueriesInfo?.reason || queriesGraphData?.reason || '';

  const formatGraphTimeLabels = useCallback((lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  }, [timeRange]);

  const graphTimeLabels = useMemo(() => formatGraphTimeLabels(graphLabels), [graphLabels, formatGraphTimeLabels]);

  const formattedGraphData = useMemo(() => {
    if (!graphLabels || graphLabels.length === 0) return [];
    return graphLabels.map((_, idx) => {
      const slowVal = graphSeries.slow_queries_total?.[idx] ?? graphSeries.slow_queries?.[idx] ?? 0;
      const point: any = {
        time: graphTimeLabels[idx] || graphLabels[idx],
        'Slow Queries': slowVal,
        slow_queries_total: slowVal,
      };
      if (!isClickHouse && !isOracle) {
        point['Deadlocks'] = graphSeries.deadlock_count?.[idx] ?? 0;
        point['Lock Waits'] = graphSeries.lock_waits?.[idx] ?? 0;
      }
      return point;
    });
  }, [graphLabels, graphSeries, graphTimeLabels, isClickHouse, isOracle]);

  const hasGraphData = useMemo(() => {
    if (!formattedGraphData || formattedGraphData.length === 0) return false;
    return formattedGraphData.some((item) =>
      Object.keys(item).some(
        (k) => k !== 'time' && typeof item[k as keyof typeof item] === 'number' && !isNaN(item[k as keyof typeof item])
      )
    );
  }, [formattedGraphData]);

  // Block I/O Telemetry Processing
  const blockLabels: string[] = blocksData?.labels || [];
  const blockSeries: Record<string, (number | null)[]> = blocksData?.series || {};
  const blockDatabases: string[] = blocksData?.databases || [];

  const blockTimeLabels = useMemo(() => formatGraphTimeLabels(blockLabels), [blockLabels, formatGraphTimeLabels]);

  // Database Block statistics breakdown
  const blockDbStats = useMemo(() => {
    if (!blockDatabases || blockDatabases.length === 0) return [];
    return blockDatabases.map((db) => {
      const hitArr = (blockSeries[`${db}:shared_blks_hit`] || []).filter((v): v is number => v !== null && v !== undefined);
      const readArr = (blockSeries[`${db}:shared_blks_read`] || []).filter((v): v is number => v !== null && v !== undefined);
      const dirtArr = (blockSeries[`${db}:shared_blks_dirtied`] || []).filter((v): v is number => v !== null && v !== undefined);

      const totalHits = hitArr.reduce((a, b) => a + b, 0);
      const totalReads = readArr.reduce((a, b) => a + b, 0);
      const totalDirtied = dirtArr.reduce((a, b) => a + b, 0);
      const peakReads = readArr.length > 0 ? Math.max(...readArr) : 0;
      const peakHits = hitArr.length > 0 ? Math.max(...hitArr) : 0;
      const totalOps = totalHits + totalReads;
      const hitRatio = totalOps > 0 ? Math.round((totalHits / totalOps) * 100) : 100;

      return {
        database: db,
        totalHits,
        totalReads,
        totalDirtied,
        peakReads,
        peakHits,
        hitRatio,
        hasActivity: totalHits > 0 || totalReads > 0 || totalDirtied > 0,
      };
    });
  }, [blockDatabases, blockSeries]);

  const filteredBlockStats = useMemo(() => {
    return blockDbStats.filter((b) => {
      const matchesSearch =
        !blockSearchQuery.trim() ||
        b.database.toLowerCase().includes(blockSearchQuery.toLowerCase().trim());
      const matchesDb =
        selectedBlockDb === 'all' || b.database.toLowerCase() === selectedBlockDb.toLowerCase();
      return matchesSearch && matchesDb;
    });
  }, [blockDbStats, blockSearchQuery, selectedBlockDb]);

  // Aggregate Block Metrics for Chart
  const formattedBlockChartData = useMemo(() => {
    if (!blockLabels || blockLabels.length === 0) return [];

    if (blockMetricTab === 'aggregate') {
      return blockLabels.map((_, idx) => {
        let totalHits = 0;
        let totalReads = 0;
        let totalDirtied = 0;

        blockDatabases.forEach((db) => {
          if (selectedBlockDb === 'all' || selectedBlockDb === db) {
            const h = blockSeries[`${db}:shared_blks_hit`]?.[idx];
            const r = blockSeries[`${db}:shared_blks_read`]?.[idx];
            const d = blockSeries[`${db}:shared_blks_dirtied`]?.[idx];
            if (h !== null && h !== undefined) totalHits += h;
            if (r !== null && r !== undefined) totalReads += r;
            if (d !== null && d !== undefined) totalDirtied += d;
          }
        });

        return {
          time: blockTimeLabels[idx] || blockLabels[idx],
          'Buffer Cache Hits': totalHits,
          'Disk Block Reads': totalReads,
          'Dirtied Blocks': totalDirtied,
        };
      });
    }

    // Per-database breakdown for single metric
    const targetDatabases =
      selectedBlockDb === 'all'
        ? blockDatabases.slice(0, 10)
        : blockDatabases.filter((d) => d === selectedBlockDb);

    return blockLabels.map((_, idx) => {
      const point: Record<string, any> = { time: blockTimeLabels[idx] || blockLabels[idx] };
      targetDatabases.forEach((db) => {
        const val = blockSeries[`${db}:${blockMetricTab}`]?.[idx];
        point[db] = val !== null && val !== undefined ? Number(val.toFixed(2)) : 0;
      });
      return point;
    });
  }, [blockLabels, blockDatabases, blockSeries, blockTimeLabels, blockMetricTab, selectedBlockDb]);

  const hasBlockData = useMemo(() => {
    if (!formattedBlockChartData || formattedBlockChartData.length === 0) return false;
    return formattedBlockChartData.some((item) =>
      Object.keys(item).some(
        (k) => k !== 'time' && typeof item[k] === 'number' && Number(item[k]) > 0
      )
    );
  }, [formattedBlockChartData]);

  const totalBlockHits = useMemo(
    () => blockDbStats.reduce((acc, curr) => acc + curr.totalHits, 0),
    [blockDbStats]
  );
  const totalBlockReads = useMemo(
    () => blockDbStats.reduce((acc, curr) => acc + curr.totalReads, 0),
    [blockDbStats]
  );
  const totalBlockDirtied = useMemo(
    () => blockDbStats.reduce((acc, curr) => acc + curr.totalDirtied, 0),
    [blockDbStats]
  );
  const overallHitRatio = useMemo(() => {
    const total = totalBlockHits + totalBlockReads;
    if (total <= 0) return 100;
    return Math.round((totalBlockHits / total) * 100);
  }, [totalBlockHits, totalBlockReads]);

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.15;
  };

  const availableDatabases = useMemo(() => {
    const set = new Set<string>();
    topQueries.forEach((q) => {
      if (q.database) set.add(q.database);
    });
    slowQueries.forEach((q) => {
      if (q.database) set.add(q.database);
    });
    return Array.from(set).sort();
  }, [topQueries, slowQueries]);

  const toggleDatabaseSelect = (dbName: string) => {
    if (selectedDatabases.includes(dbName)) {
      setSelectedDatabases(selectedDatabases.filter((d) => d !== dbName));
    } else {
      setSelectedDatabases([...selectedDatabases, dbName]);
    }
  };

  const isAllSelected = selectedDatabases.length === 0 || selectedDatabases.length === availableDatabases.length;

  const toggleAllDatabases = () => {
    if (isAllSelected) {
      setSelectedDatabases([]);
    } else {
      setSelectedDatabases([...availableDatabases]);
    }
  };

  const filteredTopQueries = useMemo(() => {
    return topQueries.filter((q) => {
      const matchesDb = selectedDatabases.length === 0 || selectedDatabases.includes(q.database);
      const matchesSearch = !searchQuery.trim() || (q.query && q.query.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesDb && matchesSearch;
    });
  }, [topQueries, selectedDatabases, searchQuery]);

  const hasLogicalReads = useMemo(() => {
    return isOracle || topQueries.some((q) => q.logical_reads !== undefined && q.logical_reads !== null);
  }, [isOracle, topQueries]);

  const filteredActiveQueries = useMemo(() => {
    if (!activeQuerySearch.trim()) return activeQueries;
    const qLower = activeQuerySearch.toLowerCase().trim();
    return activeQueries.filter((q) =>
      (q.query && q.query.toLowerCase().includes(qLower)) ||
      (q.database && q.database.toLowerCase().includes(qLower)) ||
      (q.username && q.username.toLowerCase().includes(qLower)) ||
      (q.state && q.state.toLowerCase().includes(qLower))
    );
  }, [activeQueries, activeQuerySearch]);

  const filteredSlowQueries = useMemo(() => {
    return slowQueries.filter((q) => {
      const matchesDb = selectedDatabases.length === 0 || selectedDatabases.includes(q.database);
      const matchesSearch =
        !slowQuerySearch.trim() ||
        (q.query && q.query.toLowerCase().includes(slowQuerySearch.toLowerCase())) ||
        (q.database && q.database.toLowerCase().includes(slowQuerySearch.toLowerCase())) ||
        (q.username && q.username.toLowerCase().includes(slowQuerySearch.toLowerCase()));
      return matchesDb && matchesSearch;
    });
  }, [slowQueries, selectedDatabases, slowQuerySearch]);

  const topQueriesExportColumns = useMemo<ColumnItem[]>(() => {
    const cols: ColumnItem[] = [
      { key: 'database', label: 'Database' },
      { key: 'username', label: 'User' },
      { key: 'query', label: 'Query Text' },
      { key: 'calls', label: 'Calls' },
      { key: 'avg_time_ms', label: 'Avg Time (ms)' },
      { key: 'total_time_ms', label: 'Total Time (ms)' },
    ];
    if (hasLogicalReads) {
      cols.push({ key: 'logical_reads', label: 'Logical Reads' });
    }
    return cols;
  }, [hasLogicalReads]);

  const activeQueriesExportColumns = useMemo<ColumnItem[]>(() => {
    const cols: ColumnItem[] = [
      { key: 'database', label: 'Database' },
      { key: 'username', label: 'User' },
      { key: 'query', label: 'Query Text' },
    ];
    if (!isClickHouse) {
      cols.push({ key: 'state', label: 'State' });
    }
    cols.push(
      { key: 'duration_sec', label: 'Duration (s)' },
      { key: 'collected_at', label: 'Time' }
    );
    return cols;
  }, [isClickHouse]);

  const slowQueriesExtraParams = useMemo(() => {
    const params: Record<string, any> = {
      minutes: parseInt(timeRange || '60', 10),
    };
    if (!isAllSelected && selectedDatabases.length > 0) {
      params.database = selectedDatabases.join(',');
    }
    if (slowQuerySearch.trim()) {
      params.search = slowQuerySearch.trim();
    }
    return params;
  }, [timeRange, isAllSelected, selectedDatabases, slowQuerySearch]);

  const topQueriesExtraParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (!isAllSelected && selectedDatabases.length > 0) {
      params.database = selectedDatabases.join(',');
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    return params;
  }, [isAllSelected, selectedDatabases, searchQuery]);

  const activeQueriesExtraParams = useMemo(() => {
    const params: Record<string, any> = {
      window_minutes: parseInt(timeRange || '60', 10),
    };
    if (activeQuerySearch.trim()) {
      params.search = activeQuerySearch.trim();
    }
    return params;
  }, [timeRange, activeQuerySearch]);

  const buttonLabel = useMemo(() => {
    if (selectedDatabases.length === 0 || selectedDatabases.length === availableDatabases.length) {
      return 'All Databases';
    }
    if (selectedDatabases.length === 1) {
      return selectedDatabases[0];
    }
    return `${selectedDatabases.length} Selected`;
  }, [selectedDatabases, availableDatabases]);

  const formatDuration = (sec: number | undefined | null) => {
    if (sec === undefined || sec === null || isNaN(sec)) return '0s';
    if (sec === 0) return '0s';
    if (sec >= 3600) {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      return `${h}h ${m}m`;
    }
    if (sec >= 60) {
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60);
      return `${m}m ${s}s`;
    }
    if (sec < 1) {
      const ms = sec * 1000;
      const msStr = Number(ms.toFixed(2))
      return `${msStr}ms`;
    }
    return `${Number(sec.toFixed(3))}s`;
  };

  const formatTimestamp = (ts: string | undefined | null) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return ts;
    }
  };

  const getStateBadge = (state: string | undefined) => {
    const s = (state || 'running').toLowerCase();
    if (s === 'running') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
          Running
        </span>
      );
    }
    if (s === 'suspended' || s === 'blocked' || s === 'waiting') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/20 text-amber-400 border border-amber-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
          {state}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/20 text-blue-400 border border-blue-500/30">
        {state || 'Active'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {topQueriesReason && (
        <div className="flex items-center gap-2.5 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-xl text-amber-800 dark:text-amber-300 text-xs">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span>{topQueriesReason}</span>
        </div>
      )}

      {/* KPI Telemetry Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isClickHouse ? 'lg:grid-cols-1 max-w-sm' : 'lg:grid-cols-4'} gap-2.5`}>
        {/* SLOW QUERIES */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-gray-300 dark:hover:border-red-500/30 transition-all flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Flame className="w-3 h-3 text-rose-500 dark:text-rose-400" />
                Slow Queries
              </span>
              <span
                className={`text-[9px] font-mono px-1.5 py-0.2 rounded font-semibold border ${
                  !isSlowQueriesAvailable
                    ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                    : (graphSummary?.latest?.slow_queries_total || 0) > 0
                    ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30'
                    : 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20'
                }`}
              >
                {!isSlowQueriesAvailable
                  ? 'Not available'
                  : (graphSummary?.latest?.slow_queries_total || 0) > 0
                  ? 'Elevated'
                  : 'Optimal'}
              </span>
            </div>
            <div className="text-xl font-bold font-mono text-gray-900 dark:text-white tracking-tight my-0.5">
              {!isSlowQueriesAvailable ? (
                <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">Not available</span>
              ) : (
                graphSummary?.latest?.slow_queries_total ?? 0
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80">
            {!isSlowQueriesAvailable && slowQueriesReason ? (
              <span className="truncate" title={slowQueriesReason}>{slowQueriesReason}</span>
            ) : (
              <>
                <span>Peak: <strong className="text-rose-600 dark:text-rose-400 font-mono">{graphSummary?.peak?.slow_queries_total ?? 0}</strong></span>
                <span>Avg: <strong className="text-gray-700 dark:text-gray-200 font-mono">{graphSummary?.average?.slow_queries_total ?? 0}</strong></span>
              </>
            )}
          </div>
        </div>

        {!isClickHouse && (
          <>
            {/* DEADLOCK COUNT */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-gray-300 dark:hover:border-orange-500/30 transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3 text-orange-500 dark:text-orange-400" />
                    Deadlocks
                  </span>
                  <span
                    className={`text-[9px] font-mono px-1.5 py-0.2 rounded font-semibold border ${
                      (graphSummary?.latest?.deadlock_count || 0) > 0
                        ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30'
                        : 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20'
                    }`}
                  >
                    {(graphSummary?.latest?.deadlock_count || 0) > 0 ? 'Detected' : 'Clean'}
                  </span>
                </div>
                <div className="text-xl font-bold font-mono text-orange-600 dark:text-orange-400 tracking-tight my-0.5">
                  {graphSummary?.latest?.deadlock_count ?? 0}
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80">
                <span>Peak: <strong className="text-orange-600 dark:text-orange-400 font-mono">{graphSummary?.peak?.deadlock_count ?? 0}</strong></span>
                <span>Avg: <strong className="text-gray-700 dark:text-gray-200 font-mono">{graphSummary?.average?.deadlock_count ?? 0}</strong></span>
              </div>
            </div>

            {/* LOCK WAITS */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-gray-300 dark:hover:border-amber-500/30 transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Activity className="w-3 h-3 text-amber-500 dark:text-amber-400" />
                    Lock Waits
                  </span>
                  <span className="text-[9px] font-mono text-amber-700 dark:text-amber-400 font-semibold bg-amber-100 dark:bg-amber-500/10 px-1.5 py-0.2 rounded border border-amber-200 dark:border-amber-500/20">
                    Live
                  </span>
                </div>
                <div className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400 tracking-tight my-0.5">
                  {graphSummary?.latest?.lock_waits ?? 0}
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80">
                <span>Peak: <strong className="text-amber-600 dark:text-amber-400 font-mono">{graphSummary?.peak?.lock_waits ?? 0}</strong></span>
                <span>Avg: <strong className="text-gray-700 dark:text-gray-200 font-mono">{graphSummary?.average?.lock_waits ?? 0}</strong></span>
              </div>
            </div>

            {/* SLOW QUERY PLAN CACHE */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-gray-300 dark:hover:border-blue-500/30 transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Clock className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                    Plan Cache Slow
                  </span>
                  <span className="text-[9px] font-mono text-blue-700 dark:text-blue-400 font-semibold bg-blue-100 dark:bg-blue-500/10 px-1.5 py-0.2 rounded border border-blue-200 dark:border-blue-500/20">
                    &gt; 1.0s
                  </span>
                </div>
                <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400 tracking-tight my-0.5">
                  {slowQueriesInfo?.total ?? slowQueries.length}
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80">
                <span>Plans: <strong className="text-gray-900 dark:text-white font-mono">{slowQueries.length}</strong></span>
                <span>Excluded: <strong className="text-gray-500 dark:text-gray-400 font-mono">{slowQueriesInfo?.excluded_monitoring ?? 0}</strong></span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Query Performance Timeline Chart */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              Query performance timeline
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Query volume, latency and failures
            </p>
          </div>

          {/* Metric View Mode Filters */}
          {!isClickHouse && (
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900/90 p-1 rounded-lg border border-gray-200 dark:border-gray-700/80">
              <button
                onClick={() => setGraphFilter('all')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${graphFilter === 'all'
                    ? 'bg-orange-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                All Series
              </button>
              <button
                onClick={() => setGraphFilter('slow')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${graphFilter === 'slow'
                    ? 'bg-rose-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Slow Queries
              </button>
              <button
                onClick={() => setGraphFilter('deadlocks')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${graphFilter === 'deadlocks'
                    ? 'bg-orange-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Deadlocks
              </button>
              <button
                onClick={() => setGraphFilter('locks')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${graphFilter === 'locks'
                    ? 'bg-amber-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Lock Waits
              </button>
            </div>
          )}
        </div>

        <div className="h-64 w-full flex items-center justify-center">
          {hasGraphData ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedGraphData}>
                <defs>
                  <linearGradient id="slowQueryGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="deadlockGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="lockWaitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#eab308" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#eab308" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.25} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} allowDecimals={false} domain={[0, (dataMax: number) => Math.max(dataMax || 0, 1)]}>
                  <Label value="Count / Events" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                </YAxis>
                <Tooltip
                  wrapperStyle={{ zIndex: 1000 }}
                  contentStyle={{
                    backgroundColor: '#111827',
                    borderColor: '#374151',
                    borderRadius: '8px',
                    fontSize: '11px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
                  }}
                  formatter={(value: any, name?: any) => [`${value} count`, name || 'Metric']}
                />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredGraphSeries}
                      onHoverSeries={setHoveredGraphSeries}
                    />
                  }
                />

                {(isClickHouse || graphFilter === 'all' || graphFilter === 'slow') && (
                  <Area
                    type="monotone"
                    dataKey="Slow Queries"
                    name="Slow Queries"
                    stroke="#f43f5e"
                    strokeWidth={hoveredGraphSeries === 'Slow Queries' ? 2.5 : 2}
                    fill="url(#slowQueryGrad)"
                    fillOpacity={getOpacity(hoveredGraphSeries, 'Slow Queries')}
                    strokeOpacity={getOpacity(hoveredGraphSeries, 'Slow Queries')}
                    dot={false}
                  />
                )}

                {!isClickHouse && (graphFilter === 'all' || graphFilter === 'deadlocks') && (
                  <Area
                    type="monotone"
                    dataKey="Deadlocks"
                    name="Deadlocks"
                    stroke="#f97316"
                    strokeWidth={hoveredGraphSeries === 'Deadlocks' ? 2.5 : 2}
                    fill="url(#deadlockGrad)"
                    fillOpacity={getOpacity(hoveredGraphSeries, 'Deadlocks')}
                    strokeOpacity={getOpacity(hoveredGraphSeries, 'Deadlocks')}
                    dot={false}
                  />
                )}

                {!isClickHouse && (graphFilter === 'all' || graphFilter === 'locks') && (
                  <Area
                    type="monotone"
                    dataKey="Lock Waits"
                    name="Lock Waits"
                    stroke="#eab308"
                    strokeWidth={hoveredGraphSeries === 'Lock Waits' ? 2.5 : 2}
                    fill="url(#lockWaitGrad)"
                    fillOpacity={getOpacity(hoveredGraphSeries, 'Lock Waits')}
                    strokeOpacity={getOpacity(hoveredGraphSeries, 'Lock Waits')}
                    dot={false}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-500 py-10">
              <Activity className="w-8 h-8 text-gray-600 mb-2 opacity-50" />
              <span className="text-xs italic">No query performance telemetry samples recorded</span>
            </div>
          )}
        </div>
      </div>

      {/* Block I/O / Buffer Blocks Section */}
      {!isClickHouse && !isMariaDB && !isOracle && (
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-blue-500 text-white shadow-md">
                  <HardDrive className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Block Queries & I/O Telemetry</h3>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 font-mono">
                      {blockDatabases.length} relations
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Shared buffer block hits, disk block reads, and dirtied cache blocks per relation
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Custom Database / Relation Selector for Block Chart */}
              {blockDatabases.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5 text-gray-400" />
                  <button
                    ref={blockDbButtonRef}
                    type="button"
                    onClick={toggleBlockDbDropdown}
                    className="flex items-center justify-between gap-2 bg-gray-50 dark:bg-gray-900/90 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-900 dark:text-white cursor-pointer transition-colors w-48 shadow-sm h-8"
                  >
                    <span className="truncate font-mono text-left">
                      {selectedBlockDb === 'all' ? 'All Relations' : selectedBlockDb}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${
                        isBlockDbDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {isBlockDbDropdownOpen &&
                    createPortal(
                      <div
                        ref={blockDbDropdownRef}
                        style={{
                          position: 'absolute',
                          top: `${blockDbCoords.top}px`,
                          left: `${blockDbCoords.left}px`,
                          width: `${blockDbCoords.width}px`,
                          zIndex: 99999,
                        }}
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-72 flex flex-col animate-in fade-in slide-in-from-top-1 duration-150"
                      >
                        {/* Search box for relations */}
                        <div className="p-2 border-b border-gray-200 dark:border-gray-800">
                          <div className="relative">
                            <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              placeholder="Filter relations..."
                              value={blockDbFilterQuery}
                              onChange={(e) => setBlockDbFilterQuery(e.target.value)}
                              className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-300 dark:border-gray-700/80 rounded pl-6 pr-6 py-1 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500"
                              autoFocus
                            />
                            {blockDbFilterQuery && (
                              <button
                                onClick={() => setBlockDbFilterQuery('')}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Options List */}
                        <div className="overflow-y-auto custom-scroll py-1 max-h-52">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedBlockDb('all');
                              setIsBlockDbDropdownOpen(false);
                              setBlockDbFilterQuery('');
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between cursor-pointer transition-colors ${
                              selectedBlockDb === 'all'
                                ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 font-bold border-l-2 border-blue-500'
                                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/80'
                            }`}
                          >
                            <span className="truncate">All Relations</span>
                            {selectedBlockDb === 'all' && <Check className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />}
                          </button>

                          {blockDatabases
                            .filter(
                              (db) =>
                                !blockDbFilterQuery.trim() ||
                                db.toLowerCase().includes(blockDbFilterQuery.toLowerCase().trim())
                            )
                            .map((db) => {
                              const isSelected = selectedBlockDb === db;
                              return (
                                <button
                                  key={db}
                                  type="button"
                                  onClick={() => {
                                     setSelectedBlockDb(db);
                                     setIsBlockDbDropdownOpen(false);
                                     setBlockDbFilterQuery('');
                                  }}
                                  className={`w-full text-left px-3 py-1.5 text-xs font-mono flex items-center justify-between cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 font-bold border-l-2 border-blue-500'
                                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/80'
                                  }`}
                                  title={db}
                                >
                                  <span className="truncate">{db}</span>
                                  {isSelected && <Check className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />}
                                </button>
                              );
                            })}

                          {blockDatabases.filter(
                            (db) =>
                              !blockDbFilterQuery.trim() ||
                              db.toLowerCase().includes(blockDbFilterQuery.toLowerCase().trim())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-center text-xs text-gray-500 italic">
                              No relation matched
                            </div>
                          )}
                        </div>
                      </div>,
                      document.body
                    )}
                </div>
              )}

              {/* Metric Mode Filter */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900/90 p-1 rounded-lg border border-gray-200 dark:border-gray-700/80">
                <button
                  onClick={() => setBlockMetricTab('aggregate')}
                  className={`px-2 py-1 text-[11px] font-medium rounded transition-all ${blockMetricTab === 'aggregate'
                      ? 'bg-blue-600 text-white font-bold shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  All Metrics
                </button>
                <button
                  onClick={() => setBlockMetricTab('shared_blks_hit')}
                  className={`px-2 py-1 text-[11px] font-medium rounded transition-all ${blockMetricTab === 'shared_blks_hit'
                      ? 'bg-emerald-600 text-white font-bold shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  Hits
                </button>
                <button
                  onClick={() => setBlockMetricTab('shared_blks_read')}
                  className={`px-2 py-1 text-[11px] font-medium rounded transition-all ${blockMetricTab === 'shared_blks_read'
                      ? 'bg-amber-600 text-white font-bold shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  Reads
                </button>
                <button
                  onClick={() => setBlockMetricTab('shared_blks_dirtied')}
                  className={`px-2 py-1 text-[11px] font-medium rounded transition-all ${blockMetricTab === 'shared_blks_dirtied'
                      ? 'bg-purple-600 text-white font-bold shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  Dirtied
                </button>
              </div>
            </div>
          </div>

          {/* Block KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <div className="bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700/60 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">Cache Hit Ratio</div>
              <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400 mt-0.5">{overallHitRatio}%</div>
              <div className="text-[10px] text-gray-500 mt-1">Buffer cache effectiveness</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700/60 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">Total Buffer Hits</div>
              <div className="text-xl font-bold font-mono text-gray-900 dark:text-white mt-0.5">{totalBlockHits.toLocaleString()}</div>
              <div className="text-[10px] text-gray-500 mt-1">Blocks served from memory</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700/60 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">Total Disk Reads</div>
              <div className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400 mt-0.5">{totalBlockReads.toLocaleString()}</div>
              <div className="text-[10px] text-gray-500 mt-1">Blocks read from storage</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700/60 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">Dirtied Blocks</div>
              <div className="text-xl font-bold font-mono text-purple-600 dark:text-purple-400 mt-0.5">{totalBlockDirtied.toLocaleString()}</div>
              <div className="text-[10px] text-gray-500 mt-1">Pending background write</div>
            </div>
          </div>

          {/* Block Timeline Graph */}
          <div className="h-60 w-full flex items-center justify-center pt-2">
            {hasBlockData ? (
              <ResponsiveContainer width="100%" height="100%">
                {blockMetricTab === 'aggregate' ? (
                  <AreaChart data={formattedBlockChartData}>
                    <defs>
                      <linearGradient id="blksHitGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="blksReadGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="blksDirtGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.25} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false}>
                      <Label value="Blocks count" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                    </YAxis>
                    <Tooltip
                      wrapperStyle={{ zIndex: 1000 }}
                      contentStyle={{
                        backgroundColor: '#111827',
                        borderColor: '#374151',
                        borderRadius: '8px',
                        fontSize: '11px',
                      }}
                      formatter={(val: any, name?: any) => [`${val} blocks`, name || 'Metric']}
                    />
                    <Legend
                      content={
                        <ScrollableLegend
                          hoveredSeries={hoveredBlockSeries}
                          onHoverSeries={setHoveredBlockSeries}
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="Buffer Cache Hits"
                      stroke="#10b981"
                      strokeWidth={2}
                      fill="url(#blksHitGrad)"
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="Disk Block Reads"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      fill="url(#blksReadGrad)"
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="Dirtied Blocks"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      fill="url(#blksDirtGrad)"
                      dot={false}
                    />
                  </AreaChart>
                ) : (
                  <LineChart data={formattedBlockChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.25} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false}>
                      <Label value="Blocks" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                    </YAxis>
                    <Tooltip
                      wrapperStyle={{ zIndex: 1000 }}
                      contentStyle={{
                        backgroundColor: '#111827',
                        borderColor: '#374151',
                        borderRadius: '8px',
                        fontSize: '11px',
                      }}
                    />
                    <Legend
                      content={
                        <ScrollableLegend
                          hoveredSeries={hoveredBlockSeries}
                          onHoverSeries={setHoveredBlockSeries}
                        />
                      }
                    />
                    {(selectedBlockDb === 'all'
                      ? blockDatabases.slice(0, 10)
                      : blockDatabases.filter((d) => d === selectedBlockDb)
                    ).map((db, idx) => (
                      <Line
                        key={db}
                        type="monotone"
                        dataKey={db}
                        stroke={PALETTE[idx % PALETTE.length]}
                        strokeWidth={hoveredBlockSeries === db ? 2.5 : 1.5}
                        strokeOpacity={getOpacity(hoveredBlockSeries, db)}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-500 py-8">
                <HardDrive className="w-7 h-7 text-gray-400 dark:text-gray-600 mb-1.5 opacity-50" />
                <span className="text-xs italic">No block telemetry available for the selected range</span>
              </div>
            )}
          </div>

          {/* Database Blocks Table */}
          <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3 mb-2.5">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Relation Block Operations Breakdown
              </span>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter relation..."
                  value={blockSearchQuery}
                  onChange={(e) => setBlockSearchQuery(e.target.value)}
                  className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500/50 w-48 h-7"
                />
                {blockSearchQuery && (
                  <button
                    onClick={() => setBlockSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-56 border border-gray-200 dark:border-gray-700/50 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900">Relation / Database</th>
                    <th className="py-2 px-3 text-right bg-gray-50 dark:bg-gray-900">Buffer Hits</th>
                    <th className="py-2 px-3 text-right bg-gray-50 dark:bg-gray-900">Disk Reads</th>
                    <th className="py-2 px-3 text-right bg-gray-50 dark:bg-gray-900">Dirtied</th>
                    <th className="py-2 px-3 text-right bg-gray-50 dark:bg-gray-900">Hit Ratio</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900/40 font-mono">
                  {filteredBlockStats.length > 0 ? (
                    filteredBlockStats.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                        <td className="py-2 px-3 font-semibold text-gray-900 dark:text-gray-200">{row.database}</td>
                        <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400">{row.totalHits.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400">{row.totalReads.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-purple-600 dark:text-purple-400">{row.totalDirtied.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right font-bold text-gray-900 dark:text-white">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${row.hitRatio >= 95 ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                            }`}>
                            {row.hitRatio}%
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-gray-400 italic">
                        {blockSearchQuery ? `No relations matching "${blockSearchQuery}".` : 'No block statistics available.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Slow Queries Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-rose-500 text-white shadow-md">
              <Flame className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Slow Queries</h3>
                {!isSlowQueriesAvailable ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 font-mono">
                    Not available
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 font-mono">
                    {filteredSlowQueries.length} {filteredSlowQueries.length === 1 ? 'query' : 'queries'}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {slowQueriesInfo?.definition || 'Queries averaging over 1 second in the selected range.'}
              </p>
            </div>
          </div>

          {/* Search Slow Queries & Export */}
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search slow queries..."
                value={slowQuerySearch}
                onChange={(e) => setSlowQuerySearch(e.target.value)}
                className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-rose-500/50 w-56 h-8"
              />
              {slowQuerySearch && (
                <button
                  onClick={() => setSlowQuerySearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <DBExportModal
              endpoint="/db_monitor_dashboard/slow_queries"
              columns={slowQueriesExportColumns}
              cluster_id={activeCluster}
              client_id={clientId}
              extraParams={slowQueriesExtraParams}
              defaultSortBy="avg_time_ms"
              defaultSortOrder="desc"
              buttonText="Export"
              title="Export Slow Queries"
              subtitle="Export queries exceeding execution thresholds"
            />
          </div>
        </div>

        {/* Note info banner if available */}
        {slowQueriesInfo?.note && (
          <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-lg p-2.5 text-[11px] text-gray-600 dark:text-gray-400">
            <Info className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0 mt-0.5" />
            <span>{slowQueriesInfo.note}</span>
          </div>
        )}

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-80 border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28 bg-gray-50 dark:bg-gray-900">Database</th>
                <th className="py-2.5 px-3 w-24 bg-gray-50 dark:bg-gray-900">User</th>
                <th className="py-2.5 px-3 bg-gray-50 dark:bg-gray-900">Query Text</th>
                <th className="py-2.5 px-3 text-right w-24 bg-gray-50 dark:bg-gray-900">Calls</th>
                <th className="py-2.5 px-3 text-right w-28 bg-gray-50 dark:bg-gray-900">Avg Time</th>
                <th className="py-2.5 px-3 text-right w-32 bg-gray-50 dark:bg-gray-900">Total Time</th>
                {/* <th className="py-2.5 px-3 text-center w-28 bg-gray-50 dark:bg-gray-900">Threshold</th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {filteredSlowQueries && filteredSlowQueries.length > 0 ? (
                filteredSlowQueries.map((q, idx) => {
                  const avg = q.avg_time_ms ?? q.mean_elapsed_time_ms ?? 0;
                  const avgStr = avg > 1000 ? `${(avg / 1000).toFixed(2)}s` : `${avg.toFixed(1)}ms`;
                  const total = q.total_time_ms ?? q.total_elapsed_time ?? 0;
                  const totalStr = total > 1000 ? `${(total / 1000).toFixed(2)}s` : `${total.toFixed(0)}ms`;

                  return (
                    <tr
                      key={idx}
                      onClick={() => setSelectedQueryForModal(q)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/70 cursor-pointer transition-colors group"
                      title="Click to view full query details"
                    >
                      <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400">{q.database || '-'}</td>
                      <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400 truncate max-w-[100px]" title={q.username || ''}>
                        {q.username || '-'}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-rose-600 dark:text-rose-300 max-w-lg truncate group-hover:text-rose-700 dark:group-hover:text-rose-200 transition-colors" title="Click to view full query">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="truncate">{q.query || q.query_text || q.statement || '-'}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-600 dark:text-gray-300">{(q.calls || q.execution_count || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-rose-600 dark:text-rose-400 font-bold">{avgStr}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300 font-semibold">{totalStr}</td>
                      {/* <td className="py-2.5 px-3 text-center">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30">
                          &gt; 1s
                        </span>
                      </td> */}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    {slowQuerySearch ? (
                      <span className="italic">No slow queries matching "{slowQuerySearch}".</span>
                    ) : !isSlowQueriesAvailable ? (
                      <div className="flex flex-col items-center justify-center gap-1.5 py-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-semibold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                          Not available
                        </span>
                        {slowQueriesReason && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-lg mt-0.5 not-italic">
                            {slowQueriesReason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="italic">No queries slower than 1 s in this range</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Queries by Cumulative Execution Time */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Top Queries by Cumulative Execution Time</h3>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search by Query */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search query text..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-56 h-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Database Multi-Select Dropdown */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Database:</span>
              <button
                ref={buttonRef}
                type="button"
                onClick={toggleDbDropdown}
                className="flex items-center justify-between gap-2 bg-gray-50 dark:bg-gray-900/90 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-900 dark:text-white cursor-pointer transition-colors w-48 shadow-sm h-8"
              >
                <span className="truncate">{buttonLabel}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isDbDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDbDropdownOpen &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    style={{
                      position: 'absolute',
                      top: `${coords.top}px`,
                      left: `${coords.left}px`,
                      width: `${coords.width}px`,
                      zIndex: 99999,
                    }}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1.5 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
                  >
                    {/* Select All Option */}
                    <button
                      type="button"
                      onClick={toggleAllDatabases}
                      className="w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/80 group border-b border-gray-200 dark:border-gray-800 pb-1.5 mb-1"
                    >
                      <div className="flex items-center justify-center shrink-0">
                        {isAllSelected ? (
                          <CheckSquare size={16} className="text-orange-500 dark:text-orange-400 fill-orange-500/10" />
                        ) : (
                          <Square size={16} className="text-gray-400 group-hover:text-gray-600 dark:text-gray-700 dark:group-hover:text-gray-200" />
                        )}
                      </div>
                      <span className="text-gray-900 dark:text-gray-200 font-bold">All Databases</span>
                    </button>

                    {/* Available Databases List */}
                    {availableDatabases.map((dbName) => {
                      const isSelected = selectedDatabases.includes(dbName);
                      return (
                        <button
                          key={dbName}
                          type="button"
                          onClick={() => toggleDatabaseSelect(dbName)}
                          className="w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/80 group"
                        >
                          <div className="flex items-center justify-center shrink-0">
                            {isSelected || isAllSelected ? (
                              <CheckSquare size={16} className="text-orange-500 dark:text-orange-400 fill-orange-500/10" />
                            ) : (
                              <Square size={16} className="text-gray-400 group-hover:text-gray-600 dark:text-gray-700 dark:group-hover:text-gray-200" />
                            )}
                          </div>
                          <span className="text-gray-700 dark:text-gray-200 truncate">{dbName}</span>
                        </button>
                      );
                    })}
                  </div>,
                  document.body
                )}
            </div>

            <DBExportModal
              endpoint="/db_monitor_dashboard/top_queries"
              columns={topQueriesExportColumns}
              cluster_id={activeCluster}
              client_id={clientId}
              extraParams={topQueriesExtraParams}
              defaultSortBy="total_time_ms"
              defaultSortOrder="desc"
              buttonText="Export"
              title="Export Top Queries"
              subtitle="Export top queries by cumulative execution time"
            />
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-80 border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28 bg-gray-50 dark:bg-gray-900">Database</th>
                <th className="py-2.5 px-3 w-24 bg-gray-50 dark:bg-gray-900">User</th>
                <th className="py-2.5 px-3 bg-gray-50 dark:bg-gray-900">Query Text</th>
                <th className="py-2.5 px-3 text-right w-24 bg-gray-50 dark:bg-gray-900">Calls</th>
                <th className="py-2.5 px-3 text-right w-28 bg-gray-50 dark:bg-gray-900">Avg Time</th>
                <th className="py-2.5 px-3 text-right w-32 bg-gray-50 dark:bg-gray-900">Total Time</th>
                {hasLogicalReads && <th className="py-2.5 px-3 text-right w-28 bg-gray-50 dark:bg-gray-900">Logical Reads</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {filteredTopQueries && filteredTopQueries.length > 0 ? (
                filteredTopQueries.map((q, idx) => {
                  const avg = q.avg_time_ms || 0;
                  const avgStr = avg > 1000 ? `${(avg / 1000).toFixed(2)}s` : `${avg.toFixed(1)}ms`;
                  const total = q.total_time_ms || 0;
                  const totalStr = total > 1000 ? `${(total / 1000).toFixed(2)}s` : `${total.toFixed(0)}ms`;

                  return (
                    <tr
                      key={idx}
                      onClick={() => setSelectedQueryForModal(q)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/70 cursor-pointer transition-colors group"
                      title="Click to view full query details"
                    >
                      <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400">{q.database || '-'}</td>
                      <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400 truncate max-w-[100px]" title={q.username || ''}>
                        {q.username || '-'}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-blue-600 dark:text-blue-300 max-w-lg truncate group-hover:text-blue-700 dark:group-hover:text-blue-200 transition-colors" title="Click to view full query">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="truncate">{q.query}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-600 dark:text-gray-300">{(q.calls || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-200 font-semibold">{avgStr}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-blue-600 dark:text-blue-400 font-bold">{totalStr}</td>
                      {hasLogicalReads && (
                        <td className="py-2.5 px-3 text-right font-mono text-purple-600 dark:text-purple-400 font-medium">
                          {q.logical_reads !== undefined && q.logical_reads !== null ? Number(q.logical_reads).toLocaleString() : '—'}
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={hasLogicalReads ? 7 : 6} className="py-8 text-center text-gray-400 italic">
                    {searchQuery
                      ? `No queries matching "${searchQuery}".`
                      : selectedDatabases.length > 0
                        ? 'No queries found for selected databases.'
                        : 'No queries in this range.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Queries Table */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-emerald-500 text-white shadow-md">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active Queries</h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 font-mono">
                {filteredActiveQueries.length} {filteredActiveQueries.length === 1 ? 'query' : 'queries'}
              </span>
            </div>
          </div>

          {/* Search Active Queries & Export */}
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search active queries..."
                value={activeQuerySearch}
                onChange={(e) => setActiveQuerySearch(e.target.value)}
                className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500/50 w-56 h-8"
              />
              {activeQuerySearch && (
                <button
                  onClick={() => setActiveQuerySearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <DBExportModal
              endpoint="/db_monitor_dashboard/active_queries"
              columns={activeQueriesExportColumns}
              cluster_id={activeCluster}
              client_id={clientId}
              extraParams={activeQueriesExtraParams}
              defaultSortBy="duration_sec"
              defaultSortOrder="desc"
              buttonText="Export"
              title="Export Active Queries"
              subtitle="Export currently running and active queries"
            />
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-80 border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28 bg-gray-50 dark:bg-gray-900">Database</th>
                <th className="py-2.5 px-3 w-28 bg-gray-50 dark:bg-gray-900">User</th>
                <th className="py-2.5 px-3 bg-gray-50 dark:bg-gray-900">Query Text</th>
                {!isClickHouse && <th className="py-2.5 px-3 w-24 bg-gray-50 dark:bg-gray-900">State</th>}
                <th className="py-2.5 px-3 text-right w-24 bg-gray-50 dark:bg-gray-900">Duration</th>
                <th className="py-2.5 px-3 text-right w-28 bg-gray-50 dark:bg-gray-900">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {filteredActiveQueries && filteredActiveQueries.length > 0 ? (
                filteredActiveQueries.map((q, idx) => (
                  <tr
                    key={idx}
                    onClick={() => setSelectedQueryForModal(q)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/70 cursor-pointer transition-colors group"
                    title="Click to view full query details"
                  >
                    <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400">{q.database || '-'}</td>
                    <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400" title={q.username || ''}>
                      {q.username || '-'}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-blue-600 dark:text-blue-300 max-w-lg truncate group-hover:text-blue-700 dark:group-hover:text-blue-200 transition-colors" title="Click to view full query">
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="truncate">{q.query}</span>
                      </div>
                    </td>
                    {!isClickHouse && (
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        {getStateBadge(q.state)}
                      </td>
                    )}
                    <td className="py-2.5 px-3 text-right font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                      {formatDuration(q.duration_sec)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-500 dark:text-gray-400">
                      {formatTimestamp(q.collected_at || q.log_timestamp)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isClickHouse ? 5 : 6} className="py-8 text-center text-gray-400 italic">
                    {activeQuerySearch
                      ? `No active queries matching "${activeQuerySearch}".`
                      : 'No currently running queries detected.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full Query Modal Popup */}
      {selectedQueryForModal &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 dark:bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
              {/* Modal Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50 dark:bg-gray-900/90">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400">
                    <Database className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Query Details</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        DB: <span className="text-gray-700 dark:text-gray-200 font-mono">{selectedQueryForModal.database || '-'}</span>
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">•</span>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        User: <span className="text-gray-700 dark:text-gray-200 font-mono">{selectedQueryForModal.username || '-'}</span>
                      </span>
                      {selectedQueryForModal.hostname && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Host: <span className="text-gray-700 dark:text-gray-200 font-mono">{selectedQueryForModal.hostname}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedQueryForModal(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Metrics Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 p-4 bg-gray-50/80 dark:bg-gray-950/40 border-b border-gray-200 dark:border-gray-800">
                {selectedQueryForModal.calls !== undefined || selectedQueryForModal.execution_count !== undefined ? (
                  <>
                    <div className="bg-white dark:bg-gray-800/40 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold truncate">Calls / Executions</div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white font-mono mt-0.5">
                        {(selectedQueryForModal.calls ?? selectedQueryForModal.execution_count ?? 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800/40 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold truncate">Avg Time</div>
                      <div className="text-sm font-bold text-gray-700 dark:text-gray-200 font-mono mt-0.5">
                        {((selectedQueryForModal.avg_time_ms ?? selectedQueryForModal.mean_elapsed_time_ms) || 0) > 1000
                          ? `${(((selectedQueryForModal.avg_time_ms ?? selectedQueryForModal.mean_elapsed_time_ms) || 0) / 1000).toFixed(2)}s`
                          : `${(((selectedQueryForModal.avg_time_ms ?? selectedQueryForModal.mean_elapsed_time_ms) || 0)).toFixed(1)}ms`}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800/40 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 font-semibold truncate">Total Time</div>
                      <div className="text-sm font-bold text-blue-600 dark:text-blue-400 font-mono mt-0.5">
                        {((selectedQueryForModal.total_time_ms ?? selectedQueryForModal.total_elapsed_time) || 0) > 1000
                          ? `${(((selectedQueryForModal.total_time_ms ?? selectedQueryForModal.total_elapsed_time) || 0) / 1000).toFixed(2)}s`
                          : `${(((selectedQueryForModal.total_time_ms ?? selectedQueryForModal.total_elapsed_time) || 0)).toFixed(0)}ms`}
                      </div>
                    </div>
                    {selectedQueryForModal.logical_reads !== undefined && (
                      <div className="bg-white dark:bg-gray-800/40 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50">
                        <div className="text-[10px] uppercase tracking-wider text-purple-600 dark:text-purple-400 font-semibold truncate">Logical Reads</div>
                        <div className="text-sm font-bold text-purple-600 dark:text-purple-400 font-mono mt-0.5">
                          {Number(selectedQueryForModal.logical_reads).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {selectedQueryForModal.physical_reads !== undefined && (
                      <div className="bg-white dark:bg-gray-800/40 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50">
                        <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold truncate">Physical Reads</div>
                        <div className="text-sm font-bold text-amber-600 dark:text-amber-400 font-mono mt-0.5">
                          {Number(selectedQueryForModal.physical_reads).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">State</div>
                      <div className="mt-1">
                        {getStateBadge(selectedQueryForModal.state)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">Duration</div>
                      <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
                        {formatDuration(selectedQueryForModal.duration_sec)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Query Body */}
              <div className="p-4 flex-1 overflow-y-auto custom-scroll space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Full Query Text</span>
                  <button
                    onClick={() => handleCopyQuery(selectedQueryForModal.query || selectedQueryForModal.query_text || selectedQueryForModal.statement)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md border border-gray-200 dark:border-gray-700 transition-colors font-medium cursor-pointer"
                  >
                    {copiedQuery ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                        <span className="text-emerald-600 dark:text-emerald-400">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                        <span>Copy Query</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="bg-gray-900 dark:bg-gray-950 p-3.5 rounded-lg border border-gray-800 text-xs text-blue-300 font-mono whitespace-pre-wrap break-words leading-relaxed max-h-80 overflow-y-auto custom-scroll select-all">
                  {selectedQueryForModal.query || selectedQueryForModal.query_text || selectedQueryForModal.statement || '-'}
                </pre>
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/90 flex justify-end">
                <button
                  onClick={() => setSelectedQueryForModal(null)}
                  className="px-4 py-1.5 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-xs text-gray-700 dark:text-white font-medium rounded-lg border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default QueriesView;
